import {lazy, useMemo} from "react";
import {useTranslation} from "react-i18next";
import styled from "@emotion/styled";
import {flex, mr} from "@pg-design/helpers-css";
import {Options, SeriesMapOptions} from "highcharts";

import {IMonthDistrictStats} from "../../../../api/src/db_queries/month_district_stats_query";
import {IMonthDistrictTypeStats} from "../../../../api/src/db_queries/month_district_type_stats_query";
import {mapParamSlugToCitySlug} from "../../../../config/cities";
import {
    DistrictMapLegendItem,
    dynamicDistrictMapLegendValues
} from "../../../../utils/dynamic_district_map_legend_values";
import {findMaxValue} from "../../../../utils/misc";
import {formatColoredMapTooltip} from "../../../../utils/tooltips";
import {maps} from "../../common/app/constants/maps";
import {breakpoints} from "../../common/app/styles/breakpoints";
import {ExtendedPoint, IChartProps} from "./Chart";
import {SuspendedChart} from "./SuspendedChart";

const Chart = lazy(() => import("./ChartDefaultExport"));

export interface IDistrictsMapDetailedProps extends IChartProps {
    city: string;
    data: IMonthDistrictStats[] | IMonthDistrictTypeStats[];
    unit?: string; // pluralized translation must exist in translation files under "common" key
    valueField: string;
    legendItems?: DistrictMapLegendItem[];
    legendType?: "quantitative" | "pricing";
    legendColors?: string[];
    valueMultiplier?: number;
    hideNullValues?: boolean;
    roundValues?: boolean;
    grayNullValues?: boolean;
}

export const DistrictsMapDetailed = (props: IDistrictsMapDetailedProps) => {
    const {
        city,
        data,
        valueField,
        unit,
        legendItems,
        legendType,
        legendColors,
        valueMultiplier = 1,
        hideNullValues,
        roundValues,
        grayNullValues
    } = props;

    const {i18n} = useTranslation();
    const resolvedSlug = mapParamSlugToCitySlug(city) ?? "warszawa";
    const citySlug = resolvedSlug in maps ? resolvedSlug : "warszawa";
    const highestValue = findMaxValue(data.map((el) => el[valueField as string]) as number[]);

    let legend: DistrictMapLegendItem[] = [];

    if (!legendItems && !legendType && !legendColors) {
        throw new Error("Passing either legendItems or legendType and legendColors prop is required");
    } else if (legendItems && highestValue > 0) {
        legend = legendItems;
    } else if (legendType && legendColors && highestValue > 0) {
        legend = dynamicDistrictMapLegendValues(highestValue, legendType, legendColors);
    }

    const series: SeriesMapOptions[] = useMemo(() => {
        if (data && data.length > 0) {
            return [
                {
                    ...maps[citySlug][0],
                    data: data.map((district) => {
                        // TODO: fix type
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const value: number = district[valueField];
                        const valueWithSwappedZero = value > 0 ? value : 0.0000001;
                        const multipliedValue = valueWithSwappedZero * valueMultiplier;
                        const legendWithGrayOnNullValues: DistrictMapLegendItem[] = [
                            [0, 0, "Brak danych", "rgb(230,235,245)"],
                            ...legend
                        ];

                        const colorArray = grayNullValues ? legendWithGrayOnNullValues : legend;
                        const color = colorArray.find((el) => value >= el[0] && value <= el[1])?.[3];

                        return {
                            color,
                            id: district.slug_district,
                            value: multipliedValue
                        };
                    })
                }
            ];
        }

        return [{...maps[citySlug][0]}];
    }, [data, citySlug]);

    const options: Options = {
        chart: {
            animation: false
        },
        tooltip: {
            className: "colored-map-tooltip",
            formatter: function () {
                return formatColoredMapTooltip(this, hideNullValues, roundValues, i18n, unit);
            }
        },
        responsive: {
            rules: [
                {
                    chartOptions: {
                        chart: {
                            height: 500
                        }
                    },
                    condition: {
                        minWidth: 400
                    }
                }
            ]
        },
        plotOptions: {
            map: {
                events: {
                    click: function ({point}) {
                        window.userengage("event.[BD]_district_click", {
                            city: citySlug as string,
                            district: (point as ExtendedPoint).id
                        });
                    }
                }
            }
        }
    };

    return (
        <StyledWrapper>
            <StyledContent>
                <div>
                    <SuspendedChart>
                        <Chart {...props} customOptions={options} series={series} type="map" />
                    </SuspendedChart>
                </div>

                <StyledLegendLabels>
                    {legend.map(([_, __, label, color], i) => (
                        <div key={i}>
                            <StyledDot color={color} />
                            {label}
                        </div>
                    ))}
                </StyledLegendLabels>
            </StyledContent>
        </StyledWrapper>
    );
};

const StyledWrapper = styled.div`
    width: 100%;
    height: 100%;
    padding: 2rem;
    ${flex("center", "center")};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        align-items: flex-start;
        padding-top: 60px;
    }
`;

const StyledContent = styled.div`
    width: 100%;
    ${flex("flex-start", "flex-start")};
    flex-direction: column;
    flex-wrap: wrap;

    & > div {
        width: 100%;
    }
`;

const StyledLegendLabels = styled.div`
    ${flex("center", "center")};
    gap: 1rem;
    flex-wrap: wrap;
    padding: 2rem;
    cursor: pointer;
    margin-top: 2rem;
    line-height: 1;
    color: #333333;
    font-weight: 600;
    font-size: 1.2rem;

    @media (min-width: ${breakpoints.md}) {
        justify-content: space-around;
        gap: 2rem;
        max-width: 75%;
        margin: 0 auto;
    }

    & > div {
        display: flex;
    }
`;

const StyledDot = styled.span<{color: string}>`
    width: 12px;
    height: 12px;
    ${mr(1)}
    display: inline-block;
    border-radius: 2px;
    background: ${(props) => props.color};
`;
